import React from 'react';
import { Link } from 'react-router-dom';
import './Catoo.scss';
import './Catoo.mobile.scss';
import PricingPlans from '../PricingPlans/PricingPlans';
import { useI18n } from '../../contexts/I18nContext';

// Import images
import showcase1 from '../../assets/showcase1.png';
import showcase2 from '../../assets/showcase2.png';
import whatIsImg from '../../assets/what-is.png';
import howToCreateImg from '../../assets/how-to-create.png';
import avatar1 from '../../assets/avatar1.png';
import avatar2 from '../../assets/avatar2.png';

const Catoo = () => {
  const { t } = useI18n();

  return (
    <div className="catoo">
      {/* Hero Section */}
      <section className="hero container">
        <p className="eyebrow">
          {t('catoo.nextGenEngine')}
        </p>
        <h1>
          {t('catoo.heroTitle')}
        </h1>
        <p className="sub">
          {t('catoo.heroSubtitle')}
        </p>
        <div className="actions">
          <Link className="btn btn-primary" to="/ai-workbench">
            {t('catoo.getStartedFree')}
          </Link>
          <Link className="btn btn-ghost" to="/showcase">
            {t('catoo.viewExamples')}
          </Link>
        </div>
        <div className="hero-cards">
          <Link to="/ai-workbench?mode=text-to-image" className="card">
            <h3>{t('catoo.imageGeneration')}</h3>
            <p>{t('catoo.imageGenerationDesc')}</p>
          </Link>
          <Link to="/ai-tools/veo-video-generator" className="card">
            <h3>{t('catoo.videoGeneration')}</h3>
            <p>{t('catoo.videoGenerationDesc')}</p>
          </Link>
        </div>
      </section>

      <main>
        {/* Showcase Section */}
        <section id="showcase" className="section showcase container">
          <h2 className="nano-section-title" data-lang-en="Catoo Showcase" data-lang-zh="Catoo 作品展示">
            {t('catoo.showcaseTitle')}
          </h2>
          <p className="section-sub">
            {t('catoo.showcaseSubtitle')}
          </p>
          <div className="grid g-2 images">
            <figure className="shot">
              <img src={showcase1} alt="AI generated cinematic video by Catoo" />
              <figcaption>
                {t('catoo.showcase1Caption')}
              </figcaption>
            </figure>
            <figure className="shot">
              <img src={showcase2} alt="AI generated cinematic video by Catoo" />
              <figcaption>
                {t('catoo.showcase2Caption')}
              </figcaption>
            </figure>
          </div>
        </section>

        {/* What is Section */}
        <section id="what-is" className="section what-is container">
          <div className="grid g-2 vcenter">
            <div className="media">
              <img src={whatIsImg} alt="AI generated cinematic video by Catoo" />
            </div>
            <div className="text">
              <h2 className="nano-section-title" data-lang-en="What is Catoo" data-lang-zh="什麼是 Catoo">
                {t('catoo.whatIsTitle')}
              </h2>
              <p>
                {t('catoo.whatIsDesc')}
              </p>
              <ul className="checklist">
                <li>{t('catoo.unifiedStyle')}</li>
                <li>{t('catoo.batchJobs')}</li>
                <li>{t('catoo.teamCollaboration')}</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="section why container">
          <h2 className="nano-section-title" data-lang-en="Why Choose Catoo" data-lang-zh="為什麼選擇 Catoo">
            {t('catoo.whyChooseTitle')}
          </h2>
          <div className="grid g-3 cards">
            <div className="card">
              <h3>{t('catoo.qualityControl')}</h3>
              <p>
                {t('catoo.qualityControlDesc')}
              </p>
            </div>
            <div className="card">
              <h3>{t('catoo.highEfficiency')}</h3>
              <p>
                {t('catoo.highEfficiencyDesc')}
              </p>
            </div>
            <div className="card">
              <h3>{t('catoo.costEffective')}</h3>
              <p>
                {t('catoo.costEffectiveDesc')}
              </p>
            </div>
          </div>
        </section>

        {/* How to Create Section */}
        <section className="section how-to container">
          <div className="grid g-2 vstart">
            <div className="text">
              <h2 className="nano-section-title" data-lang-en="How to Create" data-lang-zh="如何創作">
                {t('catoo.howToCreateTitle')}
              </h2>
              <ol className="steps">
                <li>
                  <strong>{t('catoo.inputIdea')}</strong>{' '}
                  {t('catoo.inputIdeaDesc')}
                </li>
                <li>
                  <strong>{t('catoo.generateDraft')}</strong>{' '}
                  {t('catoo.generateDraftDesc')}
                </li>
                <li>
                  <strong>{t('catoo.oneClickExport')}</strong>{' '}
                  {t('catoo.oneClickExportDesc')}
                </li>
              </ol>
            </div>
            <div className="media right">
              <img src={howToCreateImg} alt="AI generated cinematic video by Catoo" />
            </div>
          </div>
        </section>

        {/* Capabilities Section */}
        <section id="capabilities" className="section capabilities container">
          <h2 className="nano-section-title" data-lang-en="Capabilities" data-lang-zh="核心能力">
            {t('catoo.capabilitiesTitle')}
          </h2>
          <div className="grid g-4 cards">
            <div className="card mini">
              <h3>{t('catoo.imageStudio')}</h3>
              <p>
                {t('catoo.imageStudioDesc')}
              </p>
            </div>
            <div className="card mini">
              <h3>{t('catoo.storyboard')}</h3>
              <p>
                {t('catoo.storyboardDesc')}
              </p>
            </div>
            <div className="card mini">
              <h3>{t('catoo.publish')}</h3>
              <p>
                {t('catoo.publishDesc')}
              </p>
            </div>
            <div className="card mini">
              <h3>{t('catoo.collaborate')}</h3>
              <p>
                {t('catoo.collaborateDesc')}
              </p>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="section stats container">
          <div className="stats-row">
            <div className="stat">
              <h3>120k+</h3>
              <p>{t('catoo.creators')}</p>
            </div>
            <div className="stat">
              <h3>8×</h3>
              <p>{t('catoo.faster')}</p>
            </div>
            <div className="stat">
              <h3>92%</h3>
              <p>{t('catoo.satisfaction')}</p>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="section pricing container">
          <h2 className="nano-section-title" data-lang-en="Pricing" data-lang-zh="定價方案">
            {t('catoo.pricingTitle')}
          </h2>
          <PricingPlans showHeader={false} showBillingToggle={true} containerClass="home-pricing" />
        </section>

        {/* Testimonials Section */}
        <section className="section testimonials container">
          <h2 className="nano-section-title" data-lang-en="What creators say" data-lang-zh="創作者怎麼說">
            {t('catoo.testimonialsTitle')}
          </h2>
          <div className="grid g-2 testimonial-grid">
            <div className="testimonial card">
              <div className="author">
                <img src={avatar1} alt="AI generated cinematic video by Catoo" />
                <div>
                  <strong>{t('catoo.echoName')}</strong>
                  <p>{t('catoo.echoTitle')}</p>
                </div>
              </div>
              <p>
                {t('catoo.echoTestimonial')}
              </p>
            </div>
            <div className="testimonial card">
              <div className="author">
                <img src={avatar2} alt="AI generated cinematic video by Catoo" />
                <div>
                  <strong>{t('catoo.novaName')}</strong>
                  <p>{t('catoo.novaTitle')}</p>
                </div>
              </div>
              <p>
                {t('catoo.novaTestimonial')}
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="section faq container">
          <h2 className="nano-section-title" data-lang-en="Frequently Asked Questions" data-lang-zh="常見問題">
            {t('catoo.faqTitle')}
          </h2>
          <div className="faq-list">
            <details open>
              <summary>
                {t('catoo.faqCommercialUse')}
              </summary>
              <p>
                {t('catoo.faqCommercialUseAnswer')}
              </p>
            </details>
            <details>
              <summary>
                {t('catoo.faqOnPremise')}
              </summary>
              <p>
                {t('catoo.faqOnPremiseAnswer')}
              </p>
            </details>
            <details>
              <summary>
                {t('catoo.faqEducationDiscount')}
              </summary>
              <p>
                {t('catoo.faqEducationDiscountAnswer')}
              </p>
            </details>
            <details>
              <summary>
                {t('catoo.faqGeminiNano')}
              </summary>
              <p>
                {t('catoo.faqGeminiNanoAnswer')}
              </p>
            </details>
            <details>
              <summary>
                {t('catoo.faqDifference')}
              </summary>
              <p>
                {t('catoo.faqDifferenceAnswer')}
              </p>
            </details>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Catoo;
